import { useRef, useState, useCallback, useEffect } from "react";
import { MoveHorizontal } from "lucide-react";
import { Reveal } from "./Reveal";
import { IMAGES, STAGING_STYLES } from "@/data/content";

const METRICS = [
  { value: "30s", label: "average staging time" },
  { value: "$0", label: "per-photo cost (vs $75–150 / room)" },
  { value: "∞ styles", label: "mid-century, industrial, wabi-sabi — whatever the seller loves" },
];

export const StagingSlider = () => {
  const [pos, setPos] = useState(55);
  const [style, setStyle] = useState(STAGING_STYLES[0]);
  const [width, setWidth] = useState(0);
  const containerRef = useRef(null);
  const dragging = useRef(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => setWidth(el.offsetWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const updateFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(100, Math.max(0, p)));
  }, []);

  const onDown = (e) => {
    dragging.current = true;
    updateFromClientX(e.touches ? e.touches[0].clientX : e.clientX);
  };
  const onMove = (e) => {
    if (!dragging.current) return;
    updateFromClientX(e.touches ? e.touches[0].clientX : e.clientX);
  };
  const onUp = () => {
    dragging.current = false;
  };

  return (
    <section className="py-24 md:py-32 border-t border-ink/10" data-testid="staging-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="max-w-3xl mb-12 md:mb-16">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.2em] text-coral mb-4">Virtual staging AI</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-ink">
              The listing photos your seller expects.
            </h2>
            <p className="mt-6 text-base md:text-lg text-ink/70 leading-relaxed">
              Upload one phone snap. Get a photorealistic, style-matched hero photo in 30 seconds —
              no photographer, no stager.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div
            ref={containerRef}
            className="relative w-full aspect-[16/10] md:aspect-[2/1] overflow-hidden select-none cursor-ew-resize border border-ink/10"
            onMouseDown={onDown}
            onMouseMove={onMove}
            onMouseUp={onUp}
            onMouseLeave={onUp}
            onTouchStart={onDown}
            onTouchMove={onMove}
            onTouchEnd={onUp}
            data-testid="staging-slider"
          >
            {/* After (full width, underneath) */}
            <img
              src={style.image}
              alt={`AI-staged ${style.label} living room by Chateaux virtual staging`}
              className="absolute inset-0 w-full h-full object-cover"
              draggable="false"
            />
            <span className="absolute top-4 right-4 z-10 bg-coral text-bone text-[11px] uppercase tracking-[0.15em] px-3 py-1.5">
              {style.label} · Chateaux staging
            </span>

            {/* Before (clipped) */}
            <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
              <img
                src={IMAGES.stagingBefore}
                alt="Empty unstaged living room before AI virtual staging"
                className="absolute inset-0 h-full object-cover"
                style={{ width: width || "100%", maxWidth: "none" }}
                draggable="false"
              />
              <span className="absolute top-4 left-4 bg-ink/80 text-bone text-[11px] uppercase tracking-[0.15em] px-3 py-1.5">
                Before
              </span>
            </div>

            {/* Handle */}
            <div className="absolute top-0 bottom-0 z-20" style={{ left: `${pos}%` }}>
              <div className="absolute inset-y-0 -left-px w-0.5 bg-bone" />
              <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-11 h-11 rounded-full bg-bone border border-ink/15 shadow-lg flex items-center justify-center">
                <MoveHorizontal className="w-5 h-5 text-ink" />
              </div>
            </div>
          </div>
        </Reveal>

        {/* Style chips */}
        <div className="mt-6 flex flex-wrap gap-2" data-testid="staging-chips">
          {STAGING_STYLES.map((s) => (
            <button
              key={s.id}
              onClick={() => setStyle(s)}
              data-testid={`style-chip-${s.id}`}
              className={`px-5 py-2.5 text-sm rounded-full border transition-colors duration-300 ${
                style.id === s.id
                  ? "bg-ink text-bone border-ink"
                  : "border-ink/20 text-ink/70 hover:border-ink"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Metrics */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 border-t border-ink/10 pt-12">
          {METRICS.map((m, i) => (
            <Reveal key={m.value} delay={i * 0.1}>
              <div>
                <div className="font-serif text-5xl md:text-6xl text-ink tracking-tight">{m.value}</div>
                <p className="mt-2 text-sm text-ink/60 leading-relaxed">{m.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
